<!--
  HistoryPeriodDialog.vue — the "Select the period" modal (go.lacak.io history button).
  Opened by the history FAB. Mirrors the source: a Preset column (Yesterday, Last week,
  Last month, Today, This week, This month), a from/to date range, and a two-month day
  calendar with range selection, plus a "Selected N day(s)" footer and Cancel/Select.
  On Select it emits { from, to } (ISO) which drives track/read for that window.
  Reconstructed from the live period picker; internal impl re-authored.
-->
<template>
  <div class="hp-backdrop" @click.self="$emit('close')">
    <div class="hp-dialog">
      <div class="hp-head">Select the period</div>
      <div class="hp-body">
        <!-- presets -->
        <div class="hp-presets">
          <div class="hp-presets-title">Preset:</div>
          <div v-for="p in presets" :key="p.key" class="hp-preset" :class="{ sel: activePreset === p.key, gap: p.key === 'today' }" @click="applyPreset(p)">{{ p.label }}</div>
        </div>

        <!-- range + calendar -->
        <div class="hp-range">
          <div class="hp-range-title">Date range:</div>
          <div class="hp-range-inputs">
            <input type="date" v-model="fromDate" @change="onManual" />
            <input type="time" class="hp-time" v-model="fromTime" @change="onManual" />
            <span class="hp-dash">—</span>
            <input type="date" v-model="toDate" @change="onManual" />
            <input type="time" class="hp-time" v-model="toTime" @change="onManual" />
          </div>

          <div class="hp-cals">
            <div v-for="(cal, ci) in calendars" :key="ci" class="hp-cal">
              <div class="hp-cal-head">
                <button v-if="ci === 0" class="hp-nav" @click="shiftMonth(-1)"><i class="material-icons">chevron_left</i></button>
                <span v-else class="hp-nav-spacer"></span>
                <span class="hp-cal-title">{{ cal.title }}</span>
                <button v-if="ci === calendars.length - 1" class="hp-nav" @click="shiftMonth(1)"><i class="material-icons">chevron_right</i></button>
                <span v-else class="hp-nav-spacer"></span>
              </div>
              <div class="hp-dow"><span v-for="(d, di) in dow" :key="di">{{ d }}</span></div>
              <div class="hp-grid">
                <button
                  v-for="(cell, i) in cal.cells"
                  :key="i"
                  class="hp-day"
                  :class="{ blank: !cell, sel: cell && isSelected(cell), inrange: cell && inRange(cell), edge: cell && isEdge(cell) }"
                  :disabled="!cell"
                  @click="pickDay(cell)"
                >{{ cell ? cell.getDate() : '' }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="hp-foot">
        <span class="hp-count">Selected {{ dayCount }} day{{ dayCount === 1 ? '' : 's' }}</span>
        <div class="hp-actions">
          <button class="hp-cancel" @click="$emit('close')">CANCEL</button>
          <button class="hp-select" @click="select">SELECT</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
function ymd(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') }
function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x }
function endOfDay(d) { const x = new Date(d); x.setHours(23, 59, 59, 999); return x }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x }

export default {
  name: 'HistoryPeriodDialog',
  props: {
    // The currently-active history window (ISO strings). When reopening via "change
    // period", the dialog seeds from these instead of snapping back to Today. Named
    // seedFrom/seedTo to avoid colliding with the internal from/to data properties.
    seedFrom: { type: String, default: null },
    seedTo: { type: String, default: null }
  },
  data() {
    const today = startOfDay(new Date())
    const hasSeed = !!(this.seedFrom && this.seedTo)
    const sFrom = hasSeed ? new Date(this.seedFrom) : today
    const sTo = hasSeed ? new Date(this.seedTo) : today
    const hhmm = (d) => String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')
    return {
      dow: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
      viewMonth: new Date(sFrom.getFullYear(), sFrom.getMonth(), 1),
      from: startOfDay(sFrom),
      to: startOfDay(sTo),
      // time-of-day for the range (matches the real dialog's From/To time selectors)
      fromTime: hasSeed ? hhmm(sFrom) : '00:00',
      toTime: hasSeed ? hhmm(sTo) : '23:59',
      activePreset: hasSeed ? null : 'today',
      presets: [
        { key: 'yesterday', label: 'Yesterday' },
        { key: 'lastweek', label: 'Last week' },
        { key: 'lastmonth', label: 'Last month' },
        { key: 'today', label: 'Today' },
        { key: 'thisweek', label: 'This week' },
        { key: 'thismonth', label: 'This month' }
      ]
    }
  },
  computed: {
    fromDate: { get() { return ymd(this.from) }, set(v) { if (v) this.from = startOfDay(new Date(v + 'T00:00:00')) } },
    toDate: { get() { return ymd(this.to) }, set(v) { if (v) this.to = startOfDay(new Date(v + 'T00:00:00')) } },
    dayCount() { return Math.max(1, Math.round((startOfDay(this.to) - startOfDay(this.from)) / 86400000) + 1) },
    calendars() {
      return [0, 1].map((off) => {
        const base = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + off, 1)
        const first = base.getDay()
        const days = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate()
        const cells = []
        for (let i = 0; i < first; i++) cells.push(null)
        for (let d = 1; d <= days; d++) cells.push(new Date(base.getFullYear(), base.getMonth(), d))
        return { title: base.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }), cells }
      })
    }
  },
  methods: {
    shiftMonth(n) { this.viewMonth = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + n, 1) },
    isSelected(d) { const t = startOfDay(d).getTime(); return t === startOfDay(this.from).getTime() || t === startOfDay(this.to).getTime() },
    isEdge(d) { return this.isSelected(d) },
    inRange(d) { const t = startOfDay(d).getTime(); return t >= startOfDay(this.from).getTime() && t <= startOfDay(this.to).getTime() },
    pickDay(d) {
      if (!d) return
      const day = startOfDay(d)
      // first click (or a completed range) → start a new range; second click → extend
      if (this._picking !== 'to') { this.from = day; this.to = day; this._picking = 'to' } else {
        if (day < this.from) { this.to = this.from; this.from = day } else this.to = day
        this._picking = 'from'
      }
      this.activePreset = null
    },
    onManual() { this.activePreset = null; if (this.to < this.from) this.to = this.from },
    applyPreset(p) {
      const today = startOfDay(new Date())
      let from = today; let to = today
      if (p.key === 'yesterday') { from = addDays(today, -1); to = addDays(today, -1) }
      else if (p.key === 'today') { from = today; to = today }
      else if (p.key === 'thisweek') { from = addDays(today, -today.getDay()); to = today }
      else if (p.key === 'lastweek') { const s = addDays(today, -today.getDay() - 7); from = s; to = addDays(s, 6) }
      else if (p.key === 'thismonth') { from = new Date(today.getFullYear(), today.getMonth(), 1); to = today }
      else if (p.key === 'lastmonth') { from = new Date(today.getFullYear(), today.getMonth() - 1, 1); to = new Date(today.getFullYear(), today.getMonth(), 0) }
      this.from = from; this.to = to; this.activePreset = p.key
      this.viewMonth = new Date(from.getFullYear(), from.getMonth(), 1)
      this._picking = 'from'
    },
    // Combine the picked day with the chosen time-of-day for the emitted window.
    withTime(day, hhmm, fallbackEnd) {
      const [h, m] = (hhmm || (fallbackEnd ? '23:59' : '00:00')).split(':').map(Number)
      const x = new Date(day); x.setHours(h || 0, m || 0, fallbackEnd ? 59 : 0, fallbackEnd ? 999 : 0); return x
    },
    select() {
      const from = this.withTime(this.from, this.fromTime, false)
      const to = this.withTime(this.to, this.toTime, true)
      this.$emit('select', { from: from.toISOString(), to: to.toISOString() })
    }
  }
}
</script>

<style scoped>
.hp-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; z-index: 1200; }
.hp-dialog { width: 700px; max-width: 96vw; background: #fff; border-radius: 8px; box-shadow: 0 8px 30px rgba(0,0,0,.3); font-family: Roboto, sans-serif; display: flex; flex-direction: column; box-sizing: border-box; }
.hp-head { font-size: 20px; font-weight: 500; color: #263238; padding: 18px 24px 8px; }
.hp-body { display: flex; padding: 8px 24px; gap: 20px; }
.hp-presets { width: 130px; flex: none; }
.hp-presets-title, .hp-range-title { font-size: 13px; color: #90a4ae; margin: 8px 0; }
.hp-preset { padding: 8px 6px; font-size: 14px; color: #37474f; cursor: pointer; border-radius: 4px; }
.hp-preset:hover { background: #f2f6f9; }
.hp-preset.sel { color: #1e88c7; font-weight: 500; }
.hp-preset.gap { margin-top: 18px; }
/* min-width:0 lets the range column (and its calendars) shrink to the dialog instead of
   forcing it wider — otherwise the native date/time inputs overflow and clip the 2nd month. */
.hp-range { flex: 1; min-width: 0; }
.hp-range-inputs { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
.hp-range-inputs input { border: 1px solid #cfd8dc; border-radius: 5px; padding: 6px 6px; font-size: 13px; color: #37474f; box-sizing: border-box; }
.hp-range-inputs input[type="date"] { width: 128px; }
.hp-range-inputs .hp-time { width: 82px; }
.hp-dash { color: #90a4ae; }
.hp-cals { display: flex; gap: 16px; }
.hp-cal { flex: 1; min-width: 0; }
.hp-cal-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.hp-cal-title { font-size: 14px; font-weight: 500; color: #37474f; }
.hp-nav { background: none; border: none; cursor: pointer; color: #78909c; display: flex; padding: 0; }
.hp-nav-spacer { width: 24px; }
.hp-dow { display: grid; grid-template-columns: repeat(7, 1fr); }
.hp-dow span { text-align: center; font-size: 11px; color: #b0bec5; padding: 4px 0; }
.hp-grid { display: grid; grid-template-columns: repeat(7, 1fr); }
.hp-day { border: none; background: none; height: 30px; font-size: 13px; color: #37474f; cursor: pointer; border-radius: 50%; }
.hp-day:hover:not(:disabled) { background: #eef6fb; }
.hp-day.blank { visibility: hidden; }
.hp-day.inrange { background: #e3f1f8; border-radius: 0; }
.hp-day.edge, .hp-day.sel { background: #1e88c7; color: #fff; border-radius: 50%; }
.hp-foot { display: flex; align-items: center; justify-content: space-between; padding: 12px 24px 18px; border-top: 1px solid #eceff1; margin-top: 6px; }
.hp-count { font-size: 13px; color: #607d8b; }
.hp-actions { display: flex; gap: 8px; }
.hp-cancel, .hp-select { border: none; background: none; font-size: 14px; font-weight: 600; cursor: pointer; padding: 8px 14px; }
.hp-cancel { color: #607d8b; }
.hp-select { color: #1e88c7; }
</style>
